import { create } from 'zustand'
import { dbStorage } from '../lib/indexedDBStorage'
import { runBackupRelevantWrite } from '../lib/backupMutationCoordinator'
import {
  createChapterStructuralRangeLayer,
  removeStructuralRange,
  upsertStructuralRange,
} from '../lib/structuralRanges'
import type {
  ChapterStructuralRangeLayer,
  StructuralRange,
} from '../types/structuralRangeTypes'

interface StructuralRangeStoreState {
  layers: Map<string, ChapterStructuralRangeLayer>
  loadingChapterIds: Set<string>
  getLayer: (chapterId: string) => ChapterStructuralRangeLayer | undefined
  loadLayer: (chapterId: string, bookId?: string) => Promise<ChapterStructuralRangeLayer>
  saveLayer: (layer: ChapterStructuralRangeLayer) => Promise<void>
  upsertRange: (params: {
    chapterId: string
    bookId?: string
    range: StructuralRange
    rowOrderById: ReadonlyMap<string, number>
  }) => Promise<ChapterStructuralRangeLayer>
  removeRange: (params: {
    chapterId: string
    rangeId: string
    rowOrderById: ReadonlyMap<string, number>
  }) => Promise<ChapterStructuralRangeLayer>
}

export const useStructuralRangeStore = create<StructuralRangeStoreState>((set, get) => ({
  layers: new Map(),
  loadingChapterIds: new Set(),

  getLayer: chapterId => get().layers.get(chapterId),

  loadLayer: async (chapterId, bookId) => {
    const cached = get().layers.get(chapterId)
    if (cached) return cached
    set(state => ({
      loadingChapterIds: new Set(state.loadingChapterIds).add(chapterId),
    }))
    try {
      const persisted = await dbStorage.get('chapter_structural_layers', chapterId)
      const layer = persisted || createChapterStructuralRangeLayer({ chapterId, bookId })
      set(state => ({
        layers: new Map(state.layers).set(chapterId, layer),
      }))
      return layer
    } finally {
      set(state => {
        const loadingChapterIds = new Set(state.loadingChapterIds)
        loadingChapterIds.delete(chapterId)
        return { loadingChapterIds }
      })
    }
  },

  saveLayer: async layer => {
    await runBackupRelevantWrite(() => dbStorage.put('chapter_structural_layers', layer))
    set(state => ({
      layers: new Map(state.layers).set(layer.chapterId, layer),
    }))
  },

  upsertRange: async ({ chapterId, bookId, range, rowOrderById }) => {
    const layer = get().layers.get(chapterId) || await get().loadLayer(chapterId, bookId)
    const updated = upsertStructuralRange({ layer, range, rowOrderById })
    await get().saveLayer(updated)
    return updated
  },

  removeRange: async ({ chapterId, rangeId, rowOrderById }) => {
    const layer = get().layers.get(chapterId) || await get().loadLayer(chapterId)
    const updated = removeStructuralRange({ layer, rangeId, rowOrderById })
    await get().saveLayer(updated)
    return updated
  },
}))
